import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Button,
  List,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  Switch,
  ListItemButton,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  CloudDownload as DownloadIcon,
  Logout as LogoutIcon,
  Receipt as PlanIcon,
  AutoAwesome as GPTIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Menu as MenuIcon,
  Edit as EditIcon,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";

interface MenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

interface UserMenuProps extends MenuProps {
  onLogout: () => void;
}

interface AppBarProps {
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
}

interface MenuItem {
  icon: React.ElementType;
  text: string;
  onClick?: () => void;
}

const useMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  return { anchorEl, handleOpen, handleClose };
};

const ModelMenu: React.FC<MenuProps> = React.memo(({ anchorEl, onClose }) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    sx={{ mt: 2, ml: 1 }}
  >
    <Box p={2} width="300px">
      <Typography variant="h6" gutterBottom>
        Model
      </Typography>
      <List>
        {[
          {
            name: "GPT-4o",
            description: "Newest and most advanced model",
            selected: true,
          },
          { name: "GPT-4", description: "Advanced model for complex tasks" },
          { name: "GPT-3.5", description: "Great for everyday tasks" },
        ].map(({ name, description, selected }) => (
          <ListItemButton key={name}>
            <ListItemIcon>
              <GPTIcon />
            </ListItemIcon>
            <ListItemText primary={name} secondary={description} />
            {selected && <CheckBoxIcon />}
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
      >
        <Typography>Temporary chat</Typography>
        <Switch />
      </Box>
    </Box>
  </Menu>
));

const UserMenu: React.FC<UserMenuProps> = React.memo(
  ({ anchorEl, onClose, onLogout }) => {
    const menuItems: (MenuItem | { isDivider: true })[] = [
      { icon: PlanIcon, text: "My plan" },
      { icon: GPTIcon, text: "My GPTs" },
      { icon: EditIcon, text: "Customize ChatGPT" },
      { icon: SettingsIcon, text: "Settings" },
      { isDivider: true },
      { icon: DownloadIcon, text: "Download the macOS app" },
      { isDivider: true },
      { icon: LogoutIcon, text: "Log out", onClick: onLogout },
    ];

    return (
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
        {menuItems.map((item, index) =>
          "isDivider" in item ? (
            <Divider key={index} />
          ) : (
            <MenuItem key={item.text} onClick={item.onClick || onClose}>
              <ListItemIcon>
                <item.icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </MenuItem>
          )
        )}
      </Menu>
    );
  }
);

const AppBarComponent: React.FC<AppBarProps> = ({
  toggleSidebar,
  isSidebarOpen,
}) => {
  const navigate = useNavigate();
  const userMenu = useMenu();
  const modelMenu = useMenu();

  const handleLogout = useCallback(() => {
    userMenu.handleClose();
    navigate("/login");
  }, [navigate, userMenu]);

  const leftIcons = useMemo(
    () =>
      !isSidebarOpen && (
        <>
          <IconButton edge="start" color="inherit" onClick={toggleSidebar}>
            <MenuIcon />
          </IconButton>
          <IconButton color="inherit">
            <EditIcon />
          </IconButton>
        </>
      ),
    [isSidebarOpen, toggleSidebar]
  );

  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar>
        {leftIcons}
        <Button
          onClick={modelMenu.handleOpen}
          color="inherit"
          endIcon={<KeyboardArrowDownIcon />}
        >
          ChatGPT 4.0
        </Button>
        <ModelMenu
          anchorEl={modelMenu.anchorEl}
          onClose={modelMenu.handleClose}
        />
        <Box flexGrow={1} />
        <IconButton edge="end" color="inherit" onClick={userMenu.handleOpen}>
          <Avatar alt="User Avatar" src="/static/images/avatar/1.jpg" />
        </IconButton>
        <UserMenu
          anchorEl={userMenu.anchorEl}
          onClose={userMenu.handleClose}
          onLogout={handleLogout}
        />
      </Toolbar>
    </AppBar>
  );
};

export default React.memo(AppBarComponent);
